import {z} from 'zod';

import {invalidateOnSuccess, metadaApi} from '@dms/api/base';

import {
  type CheckoutProfile,
  type CheckoutProfileIdArg,
  CheckoutProfileIdArgSchema,
  type CheckoutProfileIdsArg,
  CheckoutProfileIdsArgSchema,
  type CheckoutProfileListArg,
  CheckoutProfileListArgSchema,
  type CheckoutProfilePickerItem,
  CheckoutProfilePickerItemSchema,
  type CheckoutProfileRule,
  type CheckoutProfileRuleIdArg,
  CheckoutProfileRuleIdArgSchema,
  type CheckoutProfileRuleIdsArg,
  CheckoutProfileRuleIdsArgSchema,
  type CheckoutProfileRulePreview,
  CheckoutProfileRulePreviewSchema,
  CheckoutProfileRuleSchema,
  CheckoutProfileRuleWriteSchema,
  CheckoutProfileSchema,
  CheckoutProfileWriteSchema,
  type CreateCheckoutProfileArg,
  CreateCheckoutProfileArgSchema,
  type CreateCheckoutProfileRuleArg,
  CreateCheckoutProfileRuleArgSchema,
  type PreviewCheckoutProfileRuleArg,
  PreviewCheckoutProfileRuleArgSchema,
  type SwapCheckoutProfileRulePriorityArg,
  SwapCheckoutProfileRulePriorityArgSchema,
  type UpdateCheckoutProfileArg,
  UpdateCheckoutProfileArgSchema,
  type UpdateCheckoutProfileRuleArg,
  UpdateCheckoutProfileRuleArgSchema,
} from './types';

const checkoutProfilesBaseApi = metadaApi.enhanceEndpoints({
  addTagTypes: ['CheckoutProfile', 'CheckoutProfileRule'],
});

// A loaded profile or rule can be sent as the body of a new one, so the write schema strips its
// read-only fields (id, version, audit) and fills defaults. A body that fails the schema is sent
// as it is: the backend then answers with its own errors and the caller receives an ApiError,
// not a Zod error from inside the query.
const toWriteBody = <Input, Output>(
  schema: z.ZodType<Output, z.ZodTypeDef, Input>,
  body: Input
): Input | Output => {
  const parsed = schema.safeParse(body);

  return parsed.success ? parsed.data : body;
};

export const checkoutProfilesApi = checkoutProfilesBaseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    getCheckoutProfile: build.query<CheckoutProfile, CheckoutProfileIdArg>({
      query: (queryArg) => ({
        url: `/checkout/v1/checkout-profile/${queryArg.checkoutProfileId}`,
        queryArg,
      }),
      providesTags: (_result, _error, arg) => [
        {type: 'CheckoutProfile', id: arg.checkoutProfileId},
      ],
      extraOptions: {
        requestSchema: CheckoutProfileIdArgSchema,
        responseSchema: CheckoutProfileSchema,
      },
    }),
    getCheckoutProfiles: build.query<CheckoutProfilePickerItem[], CheckoutProfileListArg>({
      query: (queryArg) => ({
        url: '/checkout/v1/checkout-profiles',
        params: queryArg,
        queryArg,
      }),
      providesTags: [{type: 'CheckoutProfile', id: 'LIST'}],
      extraOptions: {
        requestSchema: CheckoutProfileListArgSchema,
        responseSchema: z.array(CheckoutProfilePickerItemSchema),
      },
    }),
    createCheckoutProfile: build.mutation<CheckoutProfile, CreateCheckoutProfileArg>({
      query: (queryArg) => ({
        url: '/checkout/v1/checkout-profile',
        method: 'POST',
        body: toWriteBody(CheckoutProfileWriteSchema, queryArg.body),
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['CheckoutProfile']),
      extraOptions: {
        requestSchema: CreateCheckoutProfileArgSchema,
        responseSchema: CheckoutProfileSchema,
      },
    }),
    updateCheckoutProfile: build.mutation<CheckoutProfile, UpdateCheckoutProfileArg>({
      query: (queryArg) => ({
        url: `/checkout/v1/checkout-profile/${queryArg.checkoutProfileId}`,
        method: 'PUT',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['CheckoutProfile', 'CheckoutProfileRule']),
      extraOptions: {
        requestSchema: UpdateCheckoutProfileArgSchema,
        responseSchema: CheckoutProfileSchema,
      },
    }),
    activateCheckoutProfiles: build.mutation<void, CheckoutProfileIdsArg>({
      query: (queryArg) => ({
        url: '/checkout/v1/checkout-profiles/activate',
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['CheckoutProfile', 'CheckoutProfileRule']),
      extraOptions: {requestSchema: CheckoutProfileIdsArgSchema, responseSchema: z.null()},
    }),
    deactivateCheckoutProfiles: build.mutation<void, CheckoutProfileIdsArg>({
      query: (queryArg) => ({
        url: '/checkout/v1/checkout-profiles/deactivate',
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['CheckoutProfile', 'CheckoutProfileRule']),
      extraOptions: {requestSchema: CheckoutProfileIdsArgSchema, responseSchema: z.null()},
    }),
    deleteCheckoutProfile: build.mutation<void, CheckoutProfileIdArg>({
      query: (queryArg) => ({
        url: `/checkout/v1/checkout-profile/${queryArg.checkoutProfileId}/delete`,
        method: 'POST',
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['CheckoutProfile']),
      extraOptions: {requestSchema: CheckoutProfileIdArgSchema, responseSchema: z.null()},
    }),
    getCheckoutProfileRule: build.query<CheckoutProfileRule, CheckoutProfileRuleIdArg>({
      query: (queryArg) => ({
        url: `/checkout/v1/checkout-profile-rule/${queryArg.checkoutProfileRuleId}`,
        queryArg,
      }),
      providesTags: (_result, _error, arg) => [
        {type: 'CheckoutProfileRule', id: arg.checkoutProfileRuleId},
      ],
      extraOptions: {
        requestSchema: CheckoutProfileRuleIdArgSchema,
        responseSchema: CheckoutProfileRuleSchema,
      },
    }),
    createCheckoutProfileRule: build.mutation<CheckoutProfileRule, CreateCheckoutProfileRuleArg>({
      query: (queryArg) => ({
        url: '/checkout/v1/checkout-profile-rule',
        method: 'POST',
        body: toWriteBody(CheckoutProfileRuleWriteSchema, queryArg.body),
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['CheckoutProfileRule']),
      extraOptions: {
        requestSchema: CreateCheckoutProfileRuleArgSchema,
        responseSchema: CheckoutProfileRuleSchema,
      },
    }),
    updateCheckoutProfileRule: build.mutation<CheckoutProfileRule, UpdateCheckoutProfileRuleArg>({
      query: (queryArg) => ({
        url: `/checkout/v1/checkout-profile-rule/${queryArg.checkoutProfileRuleId}`,
        method: 'PUT',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['CheckoutProfileRule']),
      extraOptions: {
        requestSchema: UpdateCheckoutProfileRuleArgSchema,
        responseSchema: CheckoutProfileRuleSchema,
      },
    }),
    deleteCheckoutProfileRule: build.mutation<void, CheckoutProfileRuleIdArg>({
      query: (queryArg) => ({
        url: `/checkout/v1/checkout-profile-rule/${queryArg.checkoutProfileRuleId}/delete`,
        method: 'POST',
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['CheckoutProfileRule']),
      extraOptions: {requestSchema: CheckoutProfileRuleIdArgSchema, responseSchema: z.null()},
    }),
    activateCheckoutProfileRules: build.mutation<void, CheckoutProfileRuleIdsArg>({
      query: (queryArg) => ({
        url: '/checkout/v1/checkout-profile-rules/activate',
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['CheckoutProfileRule']),
      extraOptions: {requestSchema: CheckoutProfileRuleIdsArgSchema, responseSchema: z.null()},
    }),
    deactivateCheckoutProfileRules: build.mutation<void, CheckoutProfileRuleIdsArg>({
      query: (queryArg) => ({
        url: '/checkout/v1/checkout-profile-rules/deactivate',
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['CheckoutProfileRule']),
      extraOptions: {requestSchema: CheckoutProfileRuleIdsArgSchema, responseSchema: z.null()},
    }),
    swapCheckoutProfileRulePriority: build.mutation<
      CheckoutProfileRule[],
      SwapCheckoutProfileRulePriorityArg
    >({
      query: (queryArg) => ({
        url: '/checkout/v1/checkout-profile-rules/swap-priority',
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess((_result, arg: SwapCheckoutProfileRulePriorityArg) =>
        arg.checkoutProfileRuleIds.map((id) => ({type: 'CheckoutProfileRule', id}))
      ),
      extraOptions: {
        requestSchema: SwapCheckoutProfileRulePriorityArgSchema,
        responseSchema: z.array(CheckoutProfileRuleSchema),
      },
    }),
    previewCheckoutProfileRule: build.mutation<
      CheckoutProfileRulePreview,
      PreviewCheckoutProfileRuleArg
    >({
      query: (queryArg) => ({
        url: '/checkout/v1/checkout-profile-rule/preview',
        method: 'POST',
        body: toWriteBody(PreviewCheckoutProfileRuleArgSchema.shape.body, queryArg.body),
        queryArg,
      }),
      // Metada omits empty collections and null winners in preview responses. The schema in
      // extraOptions only validates in development, so this parse fills the defaults in every build.
      transformResponse: (response: unknown) => CheckoutProfileRulePreviewSchema.parse(response),
      extraOptions: {
        requestSchema: PreviewCheckoutProfileRuleArgSchema,
        responseSchema: CheckoutProfileRulePreviewSchema,
      },
    }),
  }),
});

export const {
  useGetCheckoutProfileQuery,
  useLazyGetCheckoutProfileQuery,
  useGetCheckoutProfilesQuery,
  useCreateCheckoutProfileMutation,
  useUpdateCheckoutProfileMutation,
  useActivateCheckoutProfilesMutation,
  useDeactivateCheckoutProfilesMutation,
  useDeleteCheckoutProfileMutation,
  useGetCheckoutProfileRuleQuery,
  useCreateCheckoutProfileRuleMutation,
  useUpdateCheckoutProfileRuleMutation,
  useDeleteCheckoutProfileRuleMutation,
  useActivateCheckoutProfileRulesMutation,
  useDeactivateCheckoutProfileRulesMutation,
  useSwapCheckoutProfileRulePriorityMutation,
  usePreviewCheckoutProfileRuleMutation,
} = checkoutProfilesApi;
